class QuickBuyModal extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;


    this.root = this.querySelector('[data-quick-buy-modal]');
    this.trigger = this.closest('.ProductItem')?.querySelector('[data-quick-buy-trigger]');
    this.handle = this.root.dataset.handle;

    this.state = { loaded: false };
    this.abortController = null;
    this.flkty = null;

    this.trigger?.addEventListener('click', (e) => {
      e.preventDefault();
      this.show(e);
    });

    this.root.addEventListener('click', (e) => this.onModalClick(e));

    this._onKeydown = (e) => {
      if (e.key === 'Escape' && !this.root.hidden) this.close();
    };
    document.addEventListener('keydown', this._onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown);
    this.abortController?.abort();
    this.flkty?.destroy();
  }

  onModalClick(e) {
    // Klaviyo (React) removes clicked nodes like dropdown options before this runs; a detached target is not an outside click.
    if (!e.target.isConnected) return;

    if (e.target.closest('[data-quick-buy-close]')) {
      this.close();
      return;
    }

    if (!e.target.closest('.ProductItem__Quick-Buy__Wrapper')) {
      this.close();
      return;
    }

    const thumbnail = e.target.closest('[data-quick-buy-thumbnail]');
    if (thumbnail) {
      this.flkty?.select(parseInt(thumbnail.dataset.index, 10));
      return;
    }

    const dot = e.target.closest('[data-quick-buy-dot]');
    if (dot) {
      this.flkty?.select(parseInt(dot.dataset.index, 10));
      return;
    }

    if (e.target.closest('[data-quick-buy-prev]')) {
      this.flkty?.previous();
      return;
    }

    if (e.target.closest('[data-quick-buy-next]')) {
      this.flkty?.next();
      return;
    }

    const addToCartButton = e.target.closest('[data-action="add-to-cart"]');
    if (addToCartButton) {
      e.preventDefault();
      this.addToCart(addToCartButton.closest('form'));
    }
  }

  async extractInfo() {
    this.abortController?.abort();
    this.abortController = new AbortController();

    const res = await fetch(`/products/${this.handle}?section_id=omega-new-style-template`, {
      signal: this.abortController.signal,
    });
    if (!res.ok) throw new Error(`fetch failed: ${res.status}`);

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const template = doc.querySelector('[id^="quick-buy-content-"]');

    if (!template) throw new Error('quick-buy template not found in response');

    const contentContainer = this.root.querySelector('[data-quick-buy-content]');
    contentContainer.replaceChildren(document.importNode(template.content, true));

    this.initGallery();


    window.affirm?.ui.ready(() => window.affirm.ui.refresh());

    window.dispatchEvent(new CustomEvent('onsite-event-publish', {
      detail: {
        type: 'PAGE_CHANGE',
        payload: { currentPageUrl: `${location.origin}/__quick-buy-retry-${Date.now()}` },
      },
    }));
  }

  initGallery() {
    const slideshow = this.root.querySelector('.QuickBuy__Slideshow');
    if (!slideshow) return;

    requestAnimationFrame(() => {
      this.flkty = new Flickity(slideshow, JSON.parse(slideshow.dataset.flickityConfig));

      this.flkty.on('select', () => {
        this.root.querySelectorAll('[data-quick-buy-thumbnail]').forEach((thumb, index) => {
          thumb.classList.toggle('is-active', index === this.flkty.selectedIndex);
        });
        this.root.querySelectorAll('[data-quick-buy-dot]').forEach((dot, index) => {
          dot.classList.toggle('is-active', index === this.flkty.selectedIndex);
        });
      });
    });
  }

  async addToCart(form) {
    const button = form.querySelector('.ProductForm__AddToCart');
    if (!button || button.hasAttribute('disabled')) return;

    button.setAttribute('disabled', 'disabled');
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    try {
      await window.pdmSideCart.addToCart(Object.fromEntries(new FormData(form)));
      await window.pdmSideCart.rerender();
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      button.removeAttribute('disabled');
      document.dispatchEvent(new CustomEvent('sidecart:open'));
      this.close();
    } catch (err) {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      button.removeAttribute('disabled');
      console.error('[quick-buy] add-to-cart failed', err);
    }
  }

  close() {
    const modal = this.root;
    const wrapper = modal.querySelector('.ProductItem__Quick-Buy__Wrapper');

    modal.classList.remove('is-active');
    document.body.style.overflow = '';

    const finish = () => {
      modal.classList.remove('is-closing');
      modal.setAttribute('hidden', '');
      modal.hidden = true;

      modal.querySelector('[data-quick-buy-content]').replaceChildren();
      this.state.loaded = false;
      this.flkty?.destroy();
      this.flkty = null;
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }

    modal.classList.add('is-closing');
    wrapper.addEventListener('animationend', finish, { once: true });
  }

  async show(e) {
    const modal = this.root;
    if (!modal.hidden) return;

    modal.classList.remove('is-closing');
    modal.removeAttribute('hidden');
    modal.classList.add('is-active');
    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    if (!this.state.loaded) {
      modal.querySelector('[data-quick-buy-loading]').hidden = false;
      try {
        await this.extractInfo();
        this.state.loaded = true;
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[quick-buy] failed to load product', this.handle, err);
        this.close();
      } finally {
        modal.querySelector('[data-quick-buy-loading]').hidden = true;
      }
    }
  }
}

if (!customElements.get('quick-buy-modal')) {
  customElements.define('quick-buy-modal', QuickBuyModal);
}
